import {
    centsToFormattedDollars,
    formatCurrencyInputOnChangeWithoutDecimals,
    formatDollars,
} from '@fruitful/fruitils';
import {
    formatCentsForDollarInput,
    getMinSpendCents,
    isMoneyMapValid,
    MIN_GOALS_CENTS,
    parseDollarInputToCents,
} from '@fruitful/money-map';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Platform, Pressable, ScrollView, type TextInput } from 'react-native';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CircleMinus,
    CirclePlus,
    H1Emphasized,
    H6Regular,
    Icon,
    isNonMobileWeb,
    KeyboardCompatibleBottomSheet,
    KeyboardCompatibleBottomSheetTextInput,
    LabelRegular,
    Shadows,
    Sheet,
    Slider,
    SquarePenIcon,
    StyledBodyMediumRegularInputNoOutline,
    StyledH1EmphasizedInputNoOutline,
    TriangleAlert,
    XStack,
    YStack,
} from 'smoothie';

export interface BillItem {
    id: string;
    name: string;
    amountCents: number;
}

export interface DebtItem {
    id: string;
    name: string;
    balanceCents: number;
}

export const rebalanceAfterIncomeChange = (
    newIncomeCents: number,
    billsCents: number,
    spendCents: number,
    goalsCents: number,
    oldIncomeCents: number,
): { newSpend: number; newGoals: number } => {
    const afterBills = newIncomeCents - billsCents;
    const minSpend = getMinSpendCents(newIncomeCents);
    const wasInvalid = !isMoneyMapValid(oldIncomeCents, billsCents);

    if (spendCents + goalsCents === 0 && afterBills > 0) {
        return { newSpend: 0, newGoals: afterBills };
    }

    if (wasInvalid) {
        const newSpend = Math.min(spendCents, afterBills);
        return { newSpend, newGoals: Math.max(0, afterBills - newSpend) };
    }

    let newSpend = Math.max(minSpend, Math.min(spendCents, afterBills));
    let newGoals = Math.max(0, afterBills - newSpend);

    if (newGoals < MIN_GOALS_CENTS) {
        newGoals = MIN_GOALS_CENTS;
        newSpend = Math.max(minSpend, afterBills - newGoals);
    }

    return { newSpend, newGoals };
};

export const EditIncomeSheet = ({
    isOpen,
    onClose,
    incomeTotalCents,
    billsTotalCents,
    spendTotalCents,
    goalsTotalCents,
    onSave,
}: {
    isOpen: boolean;
    onClose: () => void;
    incomeTotalCents: number;
    billsTotalCents: number;
    spendTotalCents: number;
    goalsTotalCents: number;
    onSave: (cents: number) => void;
}) => {
    const [amount, setAmount] = useState(
        formatCentsForDollarInput(incomeTotalCents),
    );
    const [inputsEnabled, setInputsEnabled] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setAmount(formatCentsForDollarInput(incomeTotalCents));
            setInputsEnabled(false);
            const timer = setTimeout(() => {
                if (
                    Platform.OS === 'web' &&
                    document.activeElement instanceof HTMLElement
                ) {
                    document.activeElement.blur();
                }
                setInputsEnabled(true);
            }, 150);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [isOpen, incomeTotalCents]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const inputRef = useRef<any>(null);

    const handleChangeText = (text: string) => {
        const prev = amount || '$ ';
        const formatted = formatCurrencyInputOnChangeWithoutDecimals(
            prev,
            text,
        );

        const el = inputRef.current as HTMLInputElement | null;
        const cursorPos: number | null =
            typeof el?.selectionStart === 'number' ? el.selectionStart : null;

        setAmount(formatted);

        if (cursorPos != null && el != null) {
            const digitsBeforeCursor = text
                .slice(0, cursorPos)
                .replace(/[^0-9]/g, '').length;
            let newPos = formatted.length;
            let digitsSeen = 0;
            for (let i = 0; i < formatted.length; i++) {
                if (/[0-9]/.test(formatted[i] ?? '')) {
                    digitsSeen++;
                    if (digitsSeen === digitsBeforeCursor) {
                        newPos = i + 1;
                        break;
                    }
                }
            }
            requestAnimationFrame(() => {
                el.setSelectionRange(newPos, newPos);
            });
        }
    };

    const handleSave = () => {
        const cents = parseDollarInputToCents(amount);
        if (cents != null && cents > 0) {
            onSave(cents);
            onClose();
        }
    };

    const [debouncedAmount, setDebouncedAmount] = useState(amount);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const debouncedSetAmount = useCallback((value: string) => {
        if (debounceRef.current != null) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            setDebouncedAmount(value);
        }, 400);
    }, []);

    useEffect(() => {
        debouncedSetAmount(amount);
    }, [amount, debouncedSetAmount]);

    useEffect(() => {
        if (isOpen) {
            setDebouncedAmount(formatCentsForDollarInput(incomeTotalCents));
        }
    }, [isOpen, incomeTotalCents]);

    useEffect(() => {
        return () => {
            if (debounceRef.current != null) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    const debouncedCents = parseDollarInputToCents(debouncedAmount);

    interface DeltaLineItem {
        key: string;
        title: string;
        description: string;
    }

    const getDeltaLineItems = (): DeltaLineItem[] => {
        if (debouncedCents == null) return [];
        const { newSpend, newGoals } = rebalanceAfterIncomeChange(
            debouncedCents,
            billsTotalCents,
            spendTotalCents,
            goalsTotalCents,
            incomeTotalCents,
        );
        const goalsDelta = newGoals - goalsTotalCents;
        const spendDelta = newSpend - spendTotalCents;

        const items: DeltaLineItem[] = [];

        if (goalsDelta !== 0) {
            const absGoalsDelta = centsToFormattedDollars(
                Math.abs(goalsDelta),
                false,
            );
            items.push({
                key: 'goals',
                title:
                    goalsDelta > 0
                        ? `Your extra ${absGoalsDelta} will go to goals`
                        : `${absGoalsDelta} will be taken from your goals`,
                description: `New monthly goal contribution: ${centsToFormattedDollars(newGoals, false)}`,
            });
        }

        if (spendDelta !== 0) {
            const absSpendDelta = centsToFormattedDollars(
                Math.abs(spendDelta),
                false,
            );
            items.push({
                key: 'spend',
                title:
                    spendDelta > 0
                        ? `Your extra ${absSpendDelta} will go to spend`
                        : `${absSpendDelta} will be taken from your spend`,
                description: `New monthly spend: ${centsToFormattedDollars(newSpend, false)}`,
            });
        }

        return items;
    };

    const deltaLineItems = getDeltaLineItems();
    const isIncrease =
        debouncedCents != null && debouncedCents > incomeTotalCents;

    if (!isOpen) return null;

    return (
        <KeyboardCompatibleBottomSheet
            onDismiss={onClose}
            showCloseButton
            title="Monthly take-home pay"
            scrollContainerOverride={(children) => <>{children}</>}
        >
            <YStack
                gap={16}
                alignItems="center"
                pointerEvents={inputsEnabled ? 'auto' : 'none'}
            >
                <KeyboardCompatibleBottomSheetTextInput
                    ref={inputRef}
                    component={StyledH1EmphasizedInputNoOutline}
                    value={amount}
                    onChangeText={handleChangeText}
                    placeholder="$0"
                    placeholderTextColor="$black"
                    inputMode="numeric"
                    keyboardType="number-pad"
                    textAlign="center"
                    height="auto"
                />
                <BodyMediumRegular color="$gray1000" textAlign="center">
                    This should be your combined take-home pay with your partner
                </BodyMediumRegular>
                {deltaLineItems.map((item) => (
                    <XStack
                        key={item.key}
                        width="100%"
                        borderRadius={16}
                        padding={16}
                        backgroundColor={
                            isIncrease ? '$green300' : '$orange300'
                        }
                        gap={16}
                        alignItems="center"
                    >
                        <Icon
                            icon={isIncrease ? CirclePlus : CircleMinus}
                            size={24}
                            color={isIncrease ? '$green1100' : '$orange1100'}
                        />
                        <YStack gap={4} flex={1}>
                            <BodyMediumRegular>{item.title}</BodyMediumRegular>
                            <BodySmallRegular color="$gray1100">
                                {item.description}
                            </BodySmallRegular>
                        </YStack>
                    </XStack>
                ))}
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Save"
                    width="full"
                    onPress={handleSave}
                    disabled={parseDollarInputToCents(amount) == null}
                />
            </YStack>
        </KeyboardCompatibleBottomSheet>
    );
};

export const EditSpendGoalsSheet = ({
    isOpen,
    onClose,
    title,
    description,
    incomeTotalCents,
    billsTotalCents,
    spendTotalCents,
    onSave,
}: {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    description: string;
    incomeTotalCents: number;
    billsTotalCents: number;
    spendTotalCents: number;
    onSave: (spendCents: number, goalsCents: number) => void;
}) => {
    const remainingCents = Math.max(0, incomeTotalCents - billsTotalCents);
    const [localSpend, setLocalSpend] = useState(spendTotalCents);
    const [inputsEnabled, setInputsEnabled] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setLocalSpend(spendTotalCents);
            setInputsEnabled(false);
            const timer = setTimeout(() => {
                if (
                    Platform.OS === 'web' &&
                    document.activeElement instanceof HTMLElement
                ) {
                    document.activeElement.blur();
                }
                setInputsEnabled(true);
            }, 150);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [isOpen, spendTotalCents]);

    const localGoals = Math.max(0, remainingCents - localSpend);

    const stepCents = useMemo(() => {
        return remainingCents < 200000 ? 1000 : 2000;
    }, [remainingCents]);

    const roundToStep = useCallback(
        (value: number) => Math.round(value / stepCents) * stepCents,
        [stepCents],
    );

    const spendPercent =
        incomeTotalCents > 0
            ? Math.round((localSpend / incomeTotalCents) * 100)
            : 0;
    const goalsPercent =
        incomeTotalCents > 0
            ? Math.round((localGoals / incomeTotalCents) * 100)
            : 0;

    const minSpendCents = getMinSpendCents(incomeTotalCents);
    const maxSpendCents = Math.max(
        minSpendCents,
        remainingCents - MIN_GOALS_CENTS,
    );
    const sliderMin = 0;
    const sliderMax = remainingCents;

    const clampedSpend = Math.max(
        minSpendCents,
        Math.min(localSpend, maxSpendCents),
    );
    const sliderValue = useMemo(() => [clampedSpend], [clampedSpend]);

    const handleSave = () => {
        onSave(localSpend, localGoals);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            showCloseButton
            title={title}
            disableDrag
            scrollContainerOverride={(children) => <>{children}</>}
        >
            <YStack
                gap={16}
                alignItems="center"
                pointerEvents={inputsEnabled ? 'auto' : 'none'}
            >
                <BodyMediumRegular textAlign="center" color="$gray1000">
                    {description}
                </BodyMediumRegular>
                <H1Emphasized textAlign="center">
                    {centsToFormattedDollars(
                        title === 'Spend'
                            ? localSpend
                            : remainingCents - localSpend,
                        false,
                    )}
                </H1Emphasized>
                <YStack
                    width="100%"
                    padding={16}
                    backgroundColor="$white"
                    borderRadius={16}
                    gap={12}
                    {...Shadows.SMALL}
                >
                    <XStack width="100%" justifyContent="space-between">
                        <XStack gap={6} alignItems="center">
                            <YStack
                                width={8}
                                height={8}
                                borderRadius={4}
                                backgroundColor="$leaf"
                            />
                            <LabelRegular>Spend</LabelRegular>
                        </XStack>
                        <XStack gap={6} alignItems="center">
                            <LabelRegular>Goals</LabelRegular>
                            <YStack
                                width={8}
                                height={8}
                                borderRadius={4}
                                backgroundColor="$petal"
                            />
                        </XStack>
                    </XStack>

                    <Slider
                        value={sliderValue}
                        onSlideMove={(_, slideValue: number) => {
                            const clamped = Math.max(
                                minSpendCents,
                                Math.min(
                                    roundToStep(slideValue),
                                    maxSpendCents,
                                ),
                            );
                            setLocalSpend(clamped);
                        }}
                        onSlideEnd={() => {
                            const clamped = Math.max(
                                minSpendCents,
                                Math.min(
                                    roundToStep(localSpend),
                                    maxSpendCents,
                                ),
                            );
                            setLocalSpend(clamped);
                        }}
                        min={sliderMin}
                        max={sliderMax}
                        step={stepCents}
                        width="100%"
                        trackActiveColor="$green800"
                        trackColor="$orchidPink600"
                        thumbSize={24}
                    />

                    <XStack width="100%">
                        <YStack gap={2} flex={1}>
                            <BodyLargeEmphasized>
                                {formatDollars(localSpend / 100, false)}
                            </BodyLargeEmphasized>
                            <LabelRegular color="$gray1000">
                                {spendPercent}% of income
                            </LabelRegular>
                        </YStack>
                        <YStack gap={2} flex={1} alignItems="flex-end">
                            <BodyLargeEmphasized>
                                {formatDollars(localGoals / 100, false)}
                            </BodyLargeEmphasized>
                            <LabelRegular color="$gray1000">
                                {goalsPercent}% of income
                            </LabelRegular>
                        </YStack>
                    </XStack>
                </YStack>

                {clampedSpend <= minSpendCents && (
                    <XStack
                        width="100%"
                        borderRadius={16}
                        padding={16}
                        backgroundColor="$orange300"
                        gap={16}
                        alignItems="center"
                    >
                        <Icon
                            icon={TriangleAlert}
                            size={24}
                            color="$orange1100"
                        />
                        <YStack gap={4} flex={1}>
                            <BodyMediumRegular>
                                Spend should not be less than 10% of income
                            </BodyMediumRegular>
                        </YStack>
                    </XStack>
                )}

                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Save"
                    width="full"
                    onPress={handleSave}
                />
            </YStack>
        </Sheet>
    );
};

const formatWithCommas = (value: string): string => {
    const digits = value.replace(/[^0-9]/g, '');
    if (digits === '') return '';
    return Number(digits).toLocaleString('en-US');
};

export const EditBillsSheet = ({
    isOpen,
    onClose,
    billItems,
    incomeTotalCents,
    spendTotalCents,
    goalsTotalCents,
    onSave,
}: {
    isOpen: boolean;
    onClose: () => void;
    billItems: BillItem[];
    incomeTotalCents: number;
    spendTotalCents: number;
    goalsTotalCents: number;
    onSave: (updatedBills: BillItem[]) => void;
}) => {
    const [localBills, setLocalBills] = useState<BillItem[]>(billItems);
    const [inputsEnabled, setInputsEnabled] = useState(false);
    const [editingBillId, setEditingBillId] = useState<string | null>(null);
    const [mobileDraft, setMobileDraft] = useState('');
    const mobileInputRef = useRef<TextInput>(null);

    useEffect(() => {
        if (isOpen) {
            setLocalBills(billItems);
            setEditingBillId(null);
            setInputsEnabled(false);
            const timer = setTimeout(() => {
                if (
                    Platform.OS === 'web' &&
                    document.activeElement instanceof HTMLElement
                ) {
                    document.activeElement.blur();
                }
                setInputsEnabled(true);
            }, 150);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [isOpen, billItems]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const inputRefs = useRef<Record<string, any>>({});

    const formatBillCents = (cents: number | null | undefined): string => {
        if (cents == null || cents === 0) return '';
        return formatCurrencyInputOnChangeWithoutDecimals(
            '$',
            String(Math.floor(cents / 100)),
        );
    };

    const handleChangeText = (id: string, text: string) => {
        const bill = localBills.find((b) => b.id === id);
        const prev = formatBillCents(bill?.amountCents ?? 0) || '$';
        const formatted = formatCurrencyInputOnChangeWithoutDecimals(
            prev,
            text,
        );

        const el = inputRefs.current[id] as HTMLInputElement | null;
        const cursorPos: number | null =
            typeof el?.selectionStart === 'number' ? el.selectionStart : null;

        const newCents = parseDollarInputToCents(formatted) ?? 0;
        setLocalBills((prevBills) =>
            prevBills.map((b) =>
                b.id === id ? { ...b, amountCents: newCents } : b,
            ),
        );

        if (cursorPos != null && el != null) {
            const digitsBeforeCursor = text
                .slice(0, cursorPos)
                .replace(/[^0-9]/g, '').length;
            let newPos = formatted.length;
            let digitsSeen = 0;
            for (let i = 0; i < formatted.length; i++) {
                if (/[0-9]/.test(formatted[i] ?? '')) {
                    digitsSeen++;
                    if (digitsSeen === digitsBeforeCursor) {
                        newPos = i + 1;
                        break;
                    }
                }
            }
            requestAnimationFrame(() => {
                el.setSelectionRange(newPos, newPos);
            });
        }
    };

    const handleStartMobileEdit = useCallback(
        (billId: string, currentCents: number) => {
            setEditingBillId(billId);
            setMobileDraft(
                currentCents > 0
                    ? Math.round(currentCents / 100).toString()
                    : '',
            );
            setTimeout(() => mobileInputRef.current?.focus(), 500);
        },
        [],
    );

    const handleMobileSave = useCallback(() => {
        if (editingBillId == null) return;
        const cents =
            mobileDraft.length > 0
                ? Number(mobileDraft.replace(/[^0-9]/g, '')) * 100
                : 0;
        setLocalBills((prev) =>
            prev.map((b) =>
                b.id === editingBillId ? { ...b, amountCents: cents } : b,
            ),
        );
        setEditingBillId(null);
    }, [editingBillId, mobileDraft]);

    const handleMobileBack = useCallback(() => {
        setEditingBillId(null);
    }, []);

    const totalCents = localBills.reduce(
        (sum, bill) => sum + bill.amountCents,
        0,
    );

    const originalBillsTotal = billItems.reduce(
        (sum, b) => sum + b.amountCents,
        0,
    );
    const billsDelta = totalCents - originalBillsTotal;

    const deltaInfo = useMemo(() => {
        if (billsDelta === 0) return null;
        const afterBills = incomeTotalCents - totalCents;
        const minSpend = getMinSpendCents(incomeTotalCents);
        let newSpend = Math.min(spendTotalCents, afterBills);
        let newGoals = Math.max(0, afterBills - newSpend);
        if (newSpend < minSpend) {
            newSpend = minSpend;
            newGoals = Math.max(0, afterBills - newSpend);
        }
        if (newGoals < MIN_GOALS_CENTS) {
            newGoals = MIN_GOALS_CENTS;
            newSpend = Math.max(minSpend, afterBills - newGoals);
        }
        const goalsDelta = newGoals - goalsTotalCents;
        const spendDelta = newSpend - spendTotalCents;

        const items: Array<{
            key: string;
            title: string;
            description: string;
        }> = [];

        if (goalsDelta !== 0) {
            const abs = centsToFormattedDollars(Math.abs(goalsDelta), false);
            items.push({
                key: 'goals',
                title:
                    goalsDelta > 0
                        ? `${abs} extra will go to goals`
                        : `${abs} will be taken from your goals`,
                description: `New monthly goal contribution: ${centsToFormattedDollars(newGoals, false)}`,
            });
        }

        if (spendDelta !== 0) {
            const abs = centsToFormattedDollars(Math.abs(spendDelta), false);
            items.push({
                key: 'spend',
                title:
                    spendDelta > 0
                        ? `${abs} extra will go to spend`
                        : `${abs} will be taken from your spend`,
                description: `New monthly spend: ${centsToFormattedDollars(newSpend, false)}`,
            });
        }

        return { items, isDecrease: billsDelta < 0 };
    }, [
        billsDelta,
        totalCents,
        incomeTotalCents,
        spendTotalCents,
        goalsTotalCents,
    ]);

    const handleSave = () => {
        onSave(localBills);
        onClose();
    };

    if (!isOpen) return null;

    const editingBill =
        editingBillId != null
            ? localBills.find((b) => b.id === editingBillId)
            : null;

    const mobileEditView = editingBill != null && !isNonMobileWeb && (
        <YStack width="100%" gap={20}>
            <H6Regular>{editingBill.name}</H6Regular>

            <XStack
                width="100%"
                backgroundColor="$gray100"
                borderRadius={12}
                paddingHorizontal={16}
                paddingVertical={14}
                alignItems="center"
                justifyContent="space-between"
            >
                <XStack alignItems="center" flex={1}>
                    <BodyMediumRegular color="$gray1100">$ </BodyMediumRegular>
                    <KeyboardCompatibleBottomSheetTextInput
                        ref={mobileInputRef}
                        component={StyledBodyMediumRegularInputNoOutline}
                        value={formatWithCommas(mobileDraft)}
                        onChangeText={(text: string) => {
                            setMobileDraft(text.replace(/[^0-9]/g, ''));
                        }}
                        onSubmitEditing={handleMobileSave}
                        keyboardType="number-pad"
                        placeholder={
                            editingBill.amountCents > 0
                                ? formatWithCommas(
                                      Math.round(
                                          editingBill.amountCents / 100,
                                      ).toString(),
                                  )
                                : '0'
                        }
                        placeholderTextColor="#999"
                        flex={1}
                        paddingVertical={0}
                        aria-label={`Amount for ${editingBill.name}`}
                    />
                </XStack>
                {mobileDraft.length > 0 && (
                    <BodyMediumRegular
                        color="$black"
                        onPress={() => {
                            setMobileDraft('');
                            mobileInputRef.current?.focus();
                        }}
                        cursor="pointer"
                    >
                        Clear
                    </BodyMediumRegular>
                )}
            </XStack>

            <XStack width="100%" gap={12}>
                <YStack flex={1}>
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        text="Back"
                        width="full"
                        onPress={handleMobileBack}
                    />
                </YStack>
                <YStack flex={1}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Save"
                        width="full"
                        onPress={handleMobileSave}
                    />
                </YStack>
            </XStack>
        </YStack>
    );

    return (
        <KeyboardCompatibleBottomSheet
            onDismiss={onClose}
            showCloseButton
            title="Monthly bills"
            scrollContainerOverride={(children) => <>{children}</>}
        >
            {editingBill != null && !isNonMobileWeb ? (
                mobileEditView
            ) : (
                <YStack
                    flex={Platform.OS === 'web' ? 1 : undefined}
                    gap={16}
                    pointerEvents={inputsEnabled ? 'auto' : 'none'}
                >
                    <H1Emphasized textAlign="center">
                        {centsToFormattedDollars(totalCents, false)}
                    </H1Emphasized>
                    <ScrollView
                        style={Platform.OS === 'web' ? { flex: 1 } : undefined}
                        showsVerticalScrollIndicator={false}
                        keyboardShouldPersistTaps="handled"
                    >
                        <YStack
                            width="100%"
                            borderRadius={24}
                            padding={16}
                            backgroundColor="#FAFAFA"
                            gap={24}
                        >
                            {localBills.map((bill) => (
                                <XStack
                                    key={bill.id}
                                    width="100%"
                                    alignItems="center"
                                    gap={8}
                                    {...(!isNonMobileWeb && {
                                        onPress: () => {
                                            handleStartMobileEdit(
                                                bill.id,
                                                bill.amountCents,
                                            );
                                        },
                                        cursor: 'pointer' as const,
                                    })}
                                >
                                    <BodyMediumRegular
                                        flex={1}
                                        flexShrink={1}
                                        numberOfLines={1}
                                        ellipsizeMode="tail"
                                        color="$gray1000"
                                    >
                                        {bill.name}
                                    </BodyMediumRegular>
                                    {isNonMobileWeb ? (
                                        <XStack
                                            alignItems="center"
                                            gap={4}
                                            cursor="pointer"
                                            flexShrink={0}
                                        >
                                            <KeyboardCompatibleBottomSheetTextInput
                                                ref={(ref: unknown) => {
                                                    inputRefs.current[bill.id] =
                                                        ref;
                                                }}
                                                component={
                                                    StyledBodyMediumRegularInputNoOutline
                                                }
                                                value={formatBillCents(
                                                    bill.amountCents,
                                                )}
                                                onChangeText={(
                                                    text: string,
                                                ) => {
                                                    handleChangeText(
                                                        bill.id,
                                                        text,
                                                    );
                                                }}
                                                placeholder="$0"
                                                placeholderTextColor="$black"
                                                inputMode="numeric"
                                                keyboardType="number-pad"
                                                textAlign="right"
                                                height="auto"
                                                width={80}
                                            />
                                            <Pressable
                                                onPress={() => {
                                                    const el =
                                                        inputRefs.current[
                                                            bill.id
                                                        ];
                                                    if (
                                                        el != null &&
                                                        typeof el.focus ===
                                                            'function'
                                                    ) {
                                                        el.focus();
                                                    }
                                                }}
                                                accessibilityRole="button"
                                                accessibilityLabel={`Edit ${bill.name}`}
                                            >
                                                <Icon
                                                    icon={SquarePenIcon}
                                                    size={16}
                                                />
                                            </Pressable>
                                        </XStack>
                                    ) : (
                                        <XStack
                                            paddingHorizontal={8}
                                            paddingVertical={4}
                                            borderRadius={999}
                                            backgroundColor="$gray200"
                                            alignItems="center"
                                            gap={4}
                                        >
                                            <BodyMediumRegular color="$gray1100">
                                                {bill.amountCents > 0
                                                    ? centsToFormattedDollars(
                                                          bill.amountCents,
                                                          false,
                                                      )
                                                    : '$0'}
                                            </BodyMediumRegular>
                                            <Icon
                                                icon={SquarePenIcon}
                                                size={14}
                                                color="$gray1100"
                                            />
                                        </XStack>
                                    )}
                                </XStack>
                            ))}
                        </YStack>
                    </ScrollView>
                    {deltaInfo != null && (
                        <YStack gap={16} marginTop={16}>
                            {deltaInfo.items.map((item) => (
                                <XStack
                                    key={item.key}
                                    width="100%"
                                    borderRadius={16}
                                    padding={16}
                                    backgroundColor={
                                        deltaInfo.isDecrease
                                            ? '$green300'
                                            : '$orange300'
                                    }
                                    gap={16}
                                    alignItems="center"
                                >
                                    <Icon
                                        icon={
                                            deltaInfo.isDecrease
                                                ? CirclePlus
                                                : CircleMinus
                                        }
                                        size={24}
                                        color={
                                            deltaInfo.isDecrease
                                                ? '$green1100'
                                                : '$orange1100'
                                        }
                                    />
                                    <YStack gap={4} flex={1}>
                                        <BodyMediumRegular>
                                            {item.title}
                                        </BodyMediumRegular>
                                        <BodySmallRegular color="$gray1100">
                                            {item.description}
                                        </BodySmallRegular>
                                    </YStack>
                                </XStack>
                            ))}
                        </YStack>
                    )}
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Save"
                        width="full"
                        onPress={handleSave}
                    />
                </YStack>
            )}
        </KeyboardCompatibleBottomSheet>
    );
};

export const EditDebtsSheet = ({
    isOpen,
    onClose,
    debtItems,
    onSave,
}: {
    isOpen: boolean;
    onClose: () => void;
    debtItems: DebtItem[];
    onSave: (updatedDebts: DebtItem[]) => void;
}) => {
    const [localDebts, setLocalDebts] = useState<DebtItem[]>(debtItems);
    const [debtsInputsEnabled, setDebtsInputsEnabled] = useState(false);
    const [editingDebtId, setEditingDebtId] = useState<string | null>(null);
    const [mobileDebtDraft, setMobileDebtDraft] = useState('');
    const mobileDebtInputRef = useRef<TextInput>(null);

    useEffect(() => {
        if (isOpen) {
            setLocalDebts(debtItems);
            setEditingDebtId(null);
            setDebtsInputsEnabled(false);
            const timer = setTimeout(() => {
                if (
                    Platform.OS === 'web' &&
                    document.activeElement instanceof HTMLElement
                ) {
                    document.activeElement.blur();
                }
                setDebtsInputsEnabled(true);
            }, 150);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [isOpen, debtItems]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const inputRefs = useRef<Record<string, any>>({});

    const formatDebtCents = (cents: number | null | undefined): string => {
        if (cents == null || cents === 0) return '';
        return formatCurrencyInputOnChangeWithoutDecimals(
            '$',
            String(Math.floor(cents / 100)),
        );
    };

    const handleChangeText = (id: string, text: string) => {
        const debt = localDebts.find((d) => d.id === id);
        const prev = formatDebtCents(debt?.balanceCents ?? 0) || '$';
        const formatted = formatCurrencyInputOnChangeWithoutDecimals(
            prev,
            text,
        );

        const el = inputRefs.current[id] as HTMLInputElement | null;
        const cursorPos: number | null =
            typeof el?.selectionStart === 'number' ? el.selectionStart : null;

        const newCents = parseDollarInputToCents(formatted) ?? 0;
        setLocalDebts((prevDebts) =>
            prevDebts.map((d) =>
                d.id === id ? { ...d, balanceCents: newCents } : d,
            ),
        );

        if (cursorPos != null && el != null) {
            const digitsBeforeCursor = text
                .slice(0, cursorPos)
                .replace(/[^0-9]/g, '').length;
            let newPos = formatted.length;
            let digitsSeen = 0;
            for (let i = 0; i < formatted.length; i++) {
                if (/[0-9]/.test(formatted[i] ?? '')) {
                    digitsSeen++;
                    if (digitsSeen === digitsBeforeCursor) {
                        newPos = i + 1;
                        break;
                    }
                }
            }
            requestAnimationFrame(() => {
                el.setSelectionRange(newPos, newPos);
            });
        }
    };

    const handleStartMobileDebtEdit = useCallback(
        (debtId: string, currentCents: number) => {
            setEditingDebtId(debtId);
            setMobileDebtDraft(
                currentCents > 0
                    ? Math.round(currentCents / 100).toString()
                    : '',
            );
            setTimeout(() => mobileDebtInputRef.current?.focus(), 500);
        },
        [],
    );

    const handleMobileDebtSave = useCallback(() => {
        if (editingDebtId == null) return;
        const cents =
            mobileDebtDraft.length > 0
                ? Number(mobileDebtDraft.replace(/[^0-9]/g, '')) * 100
                : 0;
        setLocalDebts((prev) =>
            prev.map((d) =>
                d.id === editingDebtId ? { ...d, balanceCents: cents } : d,
            ),
        );
        setEditingDebtId(null);
    }, [editingDebtId, mobileDebtDraft]);

    const handleMobileDebtBack = useCallback(() => {
        setEditingDebtId(null);
    }, []);

    const totalCents = localDebts.reduce(
        (sum, debt) => sum + debt.balanceCents,
        0,
    );

    const handleSave = () => {
        onSave(localDebts);
        onClose();
    };

    if (!isOpen) return null;

    const editingDebt =
        editingDebtId != null
            ? localDebts.find((d) => d.id === editingDebtId)
            : null;

    const mobileDebtEditView = editingDebt != null && !isNonMobileWeb && (
        <YStack width="100%" gap={20}>
            <H6Regular>{editingDebt.name}</H6Regular>

            <XStack
                width="100%"
                backgroundColor="$gray100"
                borderRadius={12}
                paddingHorizontal={16}
                paddingVertical={14}
                alignItems="center"
                justifyContent="space-between"
            >
                <XStack alignItems="center" flex={1}>
                    <BodyMediumRegular color="$gray1100">$ </BodyMediumRegular>
                    <KeyboardCompatibleBottomSheetTextInput
                        ref={mobileDebtInputRef}
                        component={StyledBodyMediumRegularInputNoOutline}
                        value={formatWithCommas(mobileDebtDraft)}
                        onChangeText={(text: string) => {
                            setMobileDebtDraft(text.replace(/[^0-9]/g, ''));
                        }}
                        onSubmitEditing={handleMobileDebtSave}
                        keyboardType="number-pad"
                        placeholder={
                            editingDebt.balanceCents > 0
                                ? formatWithCommas(
                                      Math.round(
                                          editingDebt.balanceCents / 100,
                                      ).toString(),
                                  )
                                : '0'
                        }
                        placeholderTextColor="#999"
                        flex={1}
                        paddingVertical={0}
                        aria-label={`Amount for ${editingDebt.name}`}
                    />
                </XStack>
                {mobileDebtDraft.length > 0 && (
                    <BodyMediumRegular
                        color="$black"
                        onPress={() => {
                            setMobileDebtDraft('');
                            mobileDebtInputRef.current?.focus();
                        }}
                        cursor="pointer"
                    >
                        Clear
                    </BodyMediumRegular>
                )}
            </XStack>

            <XStack width="100%" gap={12}>
                <YStack flex={1}>
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        text="Back"
                        width="full"
                        onPress={handleMobileDebtBack}
                    />
                </YStack>
                <YStack flex={1}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Save"
                        width="full"
                        onPress={handleMobileDebtSave}
                    />
                </YStack>
            </XStack>
        </YStack>
    );

    return (
        <KeyboardCompatibleBottomSheet
            onDismiss={onClose}
            showCloseButton
            title="Debts"
            scrollContainerOverride={(children) => <>{children}</>}
        >
            {editingDebt != null && !isNonMobileWeb ? (
                mobileDebtEditView
            ) : (
                <YStack
                    gap={16}
                    alignItems="center"
                    pointerEvents={debtsInputsEnabled ? 'auto' : 'none'}
                >
                    <H1Emphasized>
                        {centsToFormattedDollars(totalCents, false)}
                    </H1Emphasized>
                    <BodyMediumRegular color="$gray1000" textAlign="center">
                        Debt with interest over 10% will be one of your money
                        system goals
                    </BodyMediumRegular>
                    <YStack
                        width="100%"
                        borderRadius={24}
                        backgroundColor="#FAFAFA"
                        overflow="hidden"
                    >
                        <XStack
                            width="100%"
                            alignItems="center"
                            backgroundColor="$gray200"
                            padding={16}
                            gap={8}
                            justifyContent="space-between"
                        >
                            <BodyMediumRegular color="$gray1000">
                                Debt type
                            </BodyMediumRegular>
                            <BodyMediumRegular color="$gray1000">
                                Remaining balance
                            </BodyMediumRegular>
                        </XStack>
                        <YStack gap={16} paddingVertical={16}>
                            {localDebts.map((debt) => (
                                <XStack
                                    key={debt.id}
                                    width="100%"
                                    alignItems="center"
                                    gap={8}
                                    paddingHorizontal={16}
                                    {...(!isNonMobileWeb && {
                                        onPress: () => {
                                            handleStartMobileDebtEdit(
                                                debt.id,
                                                debt.balanceCents,
                                            );
                                        },
                                        cursor: 'pointer' as const,
                                    })}
                                >
                                    <BodyMediumRegular
                                        flex={1}
                                        numberOfLines={1}
                                        ellipsizeMode="tail"
                                        color="$gray1000"
                                    >
                                        {debt.name}
                                    </BodyMediumRegular>
                                    {isNonMobileWeb ? (
                                        <XStack
                                            alignItems="center"
                                            gap={4}
                                            cursor="pointer"
                                        >
                                            <KeyboardCompatibleBottomSheetTextInput
                                                ref={(ref: unknown) => {
                                                    inputRefs.current[debt.id] =
                                                        ref;
                                                }}
                                                component={
                                                    StyledBodyMediumRegularInputNoOutline
                                                }
                                                value={formatDebtCents(
                                                    debt.balanceCents,
                                                )}
                                                onChangeText={(
                                                    text: string,
                                                ) => {
                                                    handleChangeText(
                                                        debt.id,
                                                        text,
                                                    );
                                                }}
                                                placeholder="$0"
                                                placeholderTextColor="$black"
                                                inputMode="numeric"
                                                keyboardType="number-pad"
                                                textAlign="right"
                                                height="auto"
                                            />
                                            <Pressable
                                                onPress={() => {
                                                    const el =
                                                        inputRefs.current[
                                                            debt.id
                                                        ];
                                                    if (
                                                        el != null &&
                                                        typeof el.focus ===
                                                            'function'
                                                    ) {
                                                        el.focus();
                                                    }
                                                }}
                                                accessibilityRole="button"
                                                accessibilityLabel={`Edit ${debt.name}`}
                                            >
                                                <Icon
                                                    icon={SquarePenIcon}
                                                    size={16}
                                                />
                                            </Pressable>
                                        </XStack>
                                    ) : (
                                        <XStack
                                            paddingHorizontal={8}
                                            paddingVertical={4}
                                            borderRadius={999}
                                            backgroundColor="$gray200"
                                            alignItems="center"
                                            gap={4}
                                        >
                                            <BodyMediumRegular color="$gray1100">
                                                {debt.balanceCents > 0
                                                    ? centsToFormattedDollars(
                                                          debt.balanceCents,
                                                          false,
                                                      )
                                                    : '$0'}
                                            </BodyMediumRegular>
                                            <Icon
                                                icon={SquarePenIcon}
                                                size={14}
                                                color="$gray1100"
                                            />
                                        </XStack>
                                    )}
                                </XStack>
                            ))}
                        </YStack>
                    </YStack>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Save"
                        width="full"
                        onPress={handleSave}
                    />
                </YStack>
            )}
        </KeyboardCompatibleBottomSheet>
    );
};
